import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { PropTypes } from '../../../utils/propTypes';
import { PopoverContent } from '../PopoverMenu';
import { Icon } from '../Icon';
import styles from './Select.module.css';

/**
 * Styled entirely with `--select-*` tokens (frontend/src/styles/globals.css),
 * so it only renders styled-correct where globals.css is loaded (the
 * design-tokens-preview today). Not wired into any live feature yet.
 *
 * Compound API:
 *   <Select value onValueChange>
 *     <SelectTrigger><SelectValue placeholder="…" /></SelectTrigger>
 *     <SelectContent>
 *       <SelectGroup>
 *         <SelectLabel>…</SelectLabel>
 *         <SelectItem value="…">…</SelectItem>
 *       </SelectGroup>
 *       <SelectSeparator />
 *     </SelectContent>
 *   </Select>
 */

const SelectContext = createContext(null);
const useSelect = () => {
  const ctx = useContext(SelectContext);
  if (!ctx) throw new Error('Select sub-components must be used within <Select>');
  return ctx;
};

/* Recursively collect { value → label node } from SelectItem descendants so a
 * closed trigger can show the selected label without the content being mounted. */
function collectLabels(children, map) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === SelectItem) {
      map[child.props.value] = child.props.children;
    } else if (child.props && child.props.children) {
      collectLabels(child.props.children, map);
    }
  });
  return map;
}

// Select — ROOT. Owns the value + open state and provides context to all
// sub-parts. Wrap the entire control in it; pass `value`/`onValueChange` for
// controlled use (or `defaultValue` for uncontrolled).
const Select = ({
  value: controlledValue,
  defaultValue = null,
  onValueChange,
  disabled = false,
  items,
  children,
}) => {
  const isControlled = controlledValue !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const value = isControlled ? controlledValue : uncontrolledValue;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  // value → label map: seed from the optional `items` prop (shadcn API), then
  // augment by scanning rendered SelectItem children.
  const labelMap = useMemo(() => {
    const map = {};
    if (Array.isArray(items)) {
      items.forEach((it) => {
        if (it && it.value !== undefined) map[it.value] = it.label;
      });
    }
    return collectLabels(children, map);
  }, [children, items]);

  const setValue = useCallback(
    (next) => {
      if (!isControlled) setUncontrolledValue(next);
      onValueChange?.(next);
      setOpen(false);
    },
    [isControlled, onValueChange]
  );

  const ctx = useMemo(
    () => ({ value, setValue, open, setOpen, triggerRef, labelMap, disabled }),
    [value, setValue, open, labelMap, disabled]
  );

  return <SelectContext.Provider value={ctx}>{children}</SelectContext.Provider>;
};

/* Sticky scroll affordance for an overflowing list. Auto-scrolls the nearest
 * scroll container (the Popover body, == this button's parentElement) while the
 * pointer is over it; shows only when there's room to scroll in `direction`. */
function ScrollButton({ direction }) {
  const ref = useRef(null);
  const timer = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const scroller = ref.current?.parentElement;
    if (!scroller) return undefined;
    const update = () => {
      const max = scroller.scrollHeight - scroller.clientHeight;
      setVisible(direction === 'up' ? scroller.scrollTop > 1 : scroller.scrollTop < max - 1);
    };
    update();
    scroller.addEventListener('scroll', update);
    const ro = new ResizeObserver(update);
    ro.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [direction]);

  const start = () => {
    const scroller = ref.current?.parentElement;
    if (!scroller) return;
    const step = direction === 'up' ? -6 : 6;
    timer.current = window.setInterval(() => {
      scroller.scrollTop += step;
    }, 16);
  };
  const stop = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);

  return (
    <div
      ref={ref}
      data-visible={visible || undefined}
      className={`${styles.scrollButton} ${direction === 'up' ? styles.scrollUp : styles.scrollDown}`}
      onPointerEnter={start}
      onPointerLeave={stop}
      aria-hidden="true"
    >
      <Icon
        name={direction === 'up' ? 'chevron-up' : 'chevron-down'}
        className={styles.scrollChevron}
      />
    </div>
  );
}

// SelectScrollUpButton / SelectScrollDownButton — sticky scroll arrows for a
// long, scrollable list (shadcn dependents). Rendered by SelectContent; also
// exported for manual composition.
const SelectScrollUpButton = () => <ScrollButton direction="up" />;
const SelectScrollDownButton = () => <ScrollButton direction="down" />;

// SelectTrigger — the clickable field button that toggles the dropdown. Holds
// the SelectValue. `size` = 'default' (h-8) | 'sm' (h-7); `className` sets width.
const SelectTrigger = ({ id, className = '', size = 'default', children, ...props }) => {
  const { open, setOpen, triggerRef, disabled } = useSelect();
  const sizeClass = size === 'sm' ? styles.triggerSm : '';

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <button
      ref={triggerRef}
      id={id}
      type="button"
      className={`${styles.trigger} ${sizeClass} ${className}`}
      onClick={() => setOpen((o) => !o)}
      onKeyDown={onKeyDown}
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      {...props}
    >
      {children}
      <Icon name="chevron-down" className={styles.chevron} />
    </button>
  );
};

// SelectValue — shows the selected option's label (resolved from the value→label
// map), or the muted `placeholder` when nothing is chosen. Lives inside the trigger.
const SelectValue = ({ placeholder = '' }) => {
  const { value, labelMap } = useSelect();
  const hasValue = value !== null && value !== undefined && value !== '';
  return (
    <span className={`${styles.value} ${hasValue ? '' : styles.placeholder}`}>
      {hasValue ? labelMap[value] ?? value : placeholder}
    </span>
  );
};

// SelectContent — the dropdown panel, rendered through the compound
// PopoverContent (bare mode) so it sits on the shared 2.0 popover surface,
// width-matched to the trigger. Wraps the groups/items as a role="listbox" and
// owns the open-focus + arrow-key roving behavior.
// `alignItemWithTrigger` is accepted for shadcn API parity. We only support the
// `false` behavior (panel aligns to the trigger edge); the `true` overlay mode
// (selected item positioned over the trigger) needs offset-positioning the
// Popover primitive doesn't do yet, so it's treated as edge-align for now.
const SelectContent = ({ children, className = '', alignItemWithTrigger = false }) => {
  void alignItemWithTrigger;
  const { open, setOpen, triggerRef } = useSelect();
  const [minWidth, setMinWidth] = useState(undefined);
  const listRef = useRef(null);

  // Match the panel width to the trigger (shadcn --anchor-width).
  useEffect(() => {
    if (open && triggerRef.current) {
      setMinWidth(Math.round(triggerRef.current.getBoundingClientRect().width));
    }
  }, [open, triggerRef]);

  // Focus the selected (else first) option when the panel opens.
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      const selected = list.querySelector('[aria-selected="true"]');
      const target = selected || list.querySelector('[role="option"]:not([aria-disabled="true"])');
      target?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Roving focus across option buttons.
  const onKeyDown = (e) => {
    const list = listRef.current;
    if (!list) return;
    const items = Array.from(
      list.querySelectorAll('[role="option"]:not([aria-disabled="true"])')
    );
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[Math.min(items.length - 1, idx + 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[Math.max(0, idx - 1)]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  };

  return (
    <PopoverContent
      bare
      open={open}
      triggerRef={triggerRef}
      onClose={() => setOpen(false)}
      align="start"
      minWidth={minWidth}
    >
      <SelectScrollUpButton />
      <ul
        ref={listRef}
        className={`${styles.content} ${className}`}
        role="listbox"
        onKeyDown={onKeyDown}
      >
        {children}
      </ul>
      <SelectScrollDownButton />
    </PopoverContent>
  );
};

// SelectGroup — groups related items together under one SelectLabel.
const SelectGroup = ({ children, className = '' }) => (
  <li className={`${styles.group} ${className}`} role="group">
    <ul className={styles.groupList}>{children}</ul>
  </li>
);

// SelectLabel — non-interactive heading for a SelectGroup (e.g. "Fruits").
const SelectLabel = ({ children, className = '' }) => (
  <li className={`${styles.label} ${className}`} aria-hidden="true">
    {children}
  </li>
);

// SelectSeparator — thin divider between groups inside the content.
const SelectSeparator = ({ className = '' }) => (
  <li className={`${styles.separator} ${className}`} role="separator" aria-hidden="true" />
);

// SelectItem — a selectable option. `value` is what onValueChange receives;
// shows a check when it's the selected value. Use `disabled` to lock one.
const SelectItem = ({ value, disabled = false, children, className = '' }) => {
  const { value: selected, setValue } = useSelect();
  const isSelected = selected === value;
  return (
    <li role="presentation">
      <button
        type="button"
        role="option"
        aria-selected={isSelected}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        className={`${styles.item} ${isSelected ? styles.itemSelected : ''} ${className}`}
        onClick={() => !disabled && setValue(value)}
      >
        <span className={styles.itemLabel}>{children}</span>
        {isSelected && <Icon name="check" className={styles.check} />}
      </button>
    </li>
  );
};

Select.propTypes = {
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onValueChange: PropTypes.func,
  disabled: PropTypes.bool,
  items: PropTypes.array,
  children: PropTypes.node,
};
SelectTrigger.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.string,
  children: PropTypes.node,
};
SelectValue.propTypes = { placeholder: PropTypes.string };
SelectContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  alignItemWithTrigger: PropTypes.bool,
};
SelectGroup.propTypes = { children: PropTypes.node, className: PropTypes.string };
SelectLabel.propTypes = { children: PropTypes.node, className: PropTypes.string };
SelectSeparator.propTypes = { className: PropTypes.string };
SelectItem.propTypes = {
  value: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default Select;
export {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
